'use client';

import { useState, useEffect } from 'react';

/**
 * A/B Testing Framework for Landing Page Variants
 *
 * Story 6.1: Marketing Landing Page with A/B Variants
 *
 * Traffic distribution:
 * - Mobile (viewport < 768px): 50% Variant B, 25% A, 25% C
 * - Desktop (viewport >= 768px): 50% Variant C, 25% A, 25% B
 */

export type ABVariant = 'A' | 'B' | 'C';
export type DeviceType = 'mobile' | 'desktop';

export interface ABCookie {
  variant: ABVariant;
  assignedAt: number;
  device: DeviceType;
}

export interface UseABVariantReturn {
  variant: ABVariant;
  device: DeviceType;
  isLoading: boolean;
}

const COOKIE_NAME = 'boty_ab_variant';
const MOBILE_BREAKPOINT = 768;

/**
 * Detect device type based on viewport width
 */
function detectDevice(): DeviceType {
  if (typeof window === 'undefined') {
    return 'desktop'; // Default for SSR
  }
  return window.innerWidth < MOBILE_BREAKPOINT ? 'mobile' : 'desktop';
}

/**
 * Generate a random variant based on weighted distribution
 *
 * Mobile weights: A=25%, B=50%, C=25%
 * Desktop weights: A=25%, B=25%, C=50%
 */
function selectVariant(device: DeviceType): ABVariant {
  const random = Math.random() * 100;

  if (device === 'mobile') {
    // Mobile: 50% B, 25% A, 25% C
    if (random < 25) return 'A';
    if (random < 75) return 'B';
    return 'C';
  } else {
    // Desktop: 50% C, 25% A, 25% B
    if (random < 25) return 'A';
    if (random < 50) return 'B';
    return 'C';
  }
}

/**
 * Parse the A/B cookie from document.cookie
 */
function parseCookie(): ABCookie | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookies = document.cookie.split(';');
  const abCookie = cookies.find((c) => c.trim().startsWith(`${COOKIE_NAME}=`));

  if (!abCookie) {
    return null;
  }

  try {
    const value = abCookie.split('=')[1];
    if (!value) {
      return null;
    }
    const decoded = decodeURIComponent(value);
    return JSON.parse(decoded) as ABCookie;
  } catch {
    return null;
  }
}

/**
 * Save the A/B cookie
 * Cookie expires in 30 days
 */
function saveCookie(data: ABCookie): void {
  if (typeof document === 'undefined') {
    return;
  }

  const encoded = encodeURIComponent(JSON.stringify(data));
  const expires = new Date();
  expires.setDate(expires.getDate() + 30);

  document.cookie = `${COOKIE_NAME}=${encoded}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

/**
 * Get or create A/B variant assignment
 *
 * If an existing assignment exists and device type matches, use it.
 * Otherwise, create a new assignment based on current device type.
 */
function getOrCreateAssignment(): ABCookie {
  const currentDevice = detectDevice();
  const existing = parseCookie();

  // Use existing assignment if device type matches
  // This ensures consistent experience across visits
  if (existing && existing.device === currentDevice) {
    return existing;
  }

  // Create new assignment
  const newAssignment: ABCookie = {
    variant: selectVariant(currentDevice),
    assignedAt: Date.now(),
    device: currentDevice,
  };

  saveCookie(newAssignment);
  return newAssignment;
}

/**
 * Hook to get the current A/B variant assignment
 *
 * Returns loading state during hydration to prevent flash of wrong variant.
 * The default variant is shown during SSR, then client assignment takes over.
 *
 * @example
 * ```tsx
 * const { variant, device, isLoading } = useABVariant();
 *
 * if (isLoading) {
 *   return <DefaultLayout>{children}</DefaultLayout>;
 * }
 *
 * switch (variant) {
 *   case 'A': return <VariantA>{children}</VariantA>;
 *   case 'B': return <VariantB>{children}</VariantB>;
 *   case 'C': return <VariantC>{children}</VariantC>;
 * }
 * ```
 */
export function useABVariant(): UseABVariantReturn {
  const [assignment, setAssignment] = useState<ABCookie | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get or create assignment on client side
    const current = getOrCreateAssignment();
    setAssignment(current);
    setIsLoading(false);
  }, []);

  // Default values during SSR/loading
  if (isLoading || !assignment) {
    return {
      variant: 'C', // Default to Variant C (closest to existing LandingLayout)
      device: 'desktop',
      isLoading: true,
    };
  }

  return {
    variant: assignment.variant,
    device: assignment.device,
    isLoading: false,
  };
}

/**
 * Force a specific variant (for testing/debugging)
 * This is useful for manual testing of each variant
 */
export function forceVariant(variant: ABVariant, device?: DeviceType): void {
  const currentDevice = device ?? detectDevice();

  const assignment: ABCookie = {
    variant,
    assignedAt: Date.now(),
    device: currentDevice,
  };

  saveCookie(assignment);

  // Reload to apply the new variant
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
}

/**
 * Clear the A/B variant assignment
 * This will cause a new assignment on next page load
 */
export function clearVariant(): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Get the current variant without using React state
 * Useful for analytics and non-React contexts
 */
export function getCurrentVariant(): ABCookie | null {
  return parseCookie();
}
